import { OptionProps } from '@mui/base';
import { Autocomplete, Button, TextField, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { MobileOpCode } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { upperFirst } from 'lodash';
import React from 'react';
import { Control, Controller, FieldValues, UseFormRegister } from 'react-hook-form';
import AutocompletePaper from 'ui/atoms/AutocompletePaper/AutocompletePaper';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

export interface Props {
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues, object>;
  handleClearFiltersButton: () => void;
  isClearButtonDisabled: boolean;

  startDateTimeValue: dayjs.Dayjs | null;
  handleStartDataTimeInputChange: (value: dayjs.Dayjs | null) => void;

  endDateTimeValue: dayjs.Dayjs | null;
  handleEndDataTimeInputChange: (value: dayjs.Dayjs | null) => void;

  showValidation: boolean;
  dataTimeRangeInputValidation: string | undefined;

  visitorSelectOptions: Array<OptionProps<null | string>>;
  visitorSelectValue?: null | OptionProps<null | string>;
  handleVisitorSelectChange: (value: null | OptionProps<null | string>) => void;

  lockSelectOptions: Array<OptionProps<null | string>>;
  lockSelectValue?: null | OptionProps<null | string>;
  handleLockSelectChange: (value: null | OptionProps<null | string>) => void;

  actionSelectOptions: Array<OptionProps<null | MobileOpCode>>;
  actionSelectValue: null | OptionProps<null | MobileOpCode>;
  handleActionSelectChange: (value: null | OptionProps<null | MobileOpCode>) => void;

  reasonSelectOptions: Array<OptionProps<null | string>>;
  reasonSelectValue: null | OptionProps<null | string>;
  handleReasonSelectChange: (value: null | OptionProps<null | string>) => void;
}

const AccessEventsFilters: React.FC<React.PropsWithChildren<Props>> = ({
  register,
  control,
  handleClearFiltersButton,
  isClearButtonDisabled,
  startDateTimeValue,
  endDateTimeValue,
  handleStartDataTimeInputChange,
  handleEndDataTimeInputChange,
  showValidation,
  dataTimeRangeInputValidation,
  visitorSelectOptions,
  visitorSelectValue,
  handleVisitorSelectChange,
  lockSelectOptions,
  lockSelectValue,
  handleLockSelectChange,
  actionSelectOptions,
  actionSelectValue,
  handleActionSelectChange,
  reasonSelectOptions,
  reasonSelectValue,
  handleReasonSelectChange
}) => {
  const { translation, getLanguage, getLocaleForDayjs } = useTranslation();
  const theme = useTheme();

  return (
    <ComponentWrapper flexDirection="column" gap="1.5rem" margin="0.5rem 0 0 0">
      <Controller
        control={control}
        name="filters.startTime"
        render={() => (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getLocaleForDayjs(getLanguage).name}>
            <DesktopDateTimePicker
              label={upperFirst(translation.start_date_time)}
              value={startDateTimeValue ? startDateTimeValue : undefined}
              views={['year', 'day', 'hours', 'minutes']}
              onChange={handleStartDataTimeInputChange}
              maxDate={endDateTimeValue ? endDateTimeValue : undefined}
              slotProps={{
                textField: {
                  helperText:
                    showValidation && !!dataTimeRangeInputValidation ? dataTimeRangeInputValidation : undefined,
                  id: 'drawer-input-start-time',
                  InputLabelProps: { shrink: true }
                }
              }}
              disableFuture
            />
          </LocalizationProvider>
        )}
      />
      <Controller
        control={control}
        name="filters.endTime"
        render={() => (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getLocaleForDayjs(getLanguage).name}>
            <DesktopDateTimePicker
              label={upperFirst(translation.end_date_time)}
              value={endDateTimeValue ? endDateTimeValue : undefined}
              views={['year', 'day', 'hours', 'minutes']}
              onChange={handleEndDataTimeInputChange}
              minDate={startDateTimeValue ? startDateTimeValue : undefined}
              slotProps={{
                textField: {
                  helperText: showValidation && dataTimeRangeInputValidation ? dataTimeRangeInputValidation : undefined,
                  id: 'drawer-input-end-time',
                  InputLabelProps: { shrink: true }
                }
              }}
              disableFuture
            />
          </LocalizationProvider>
        )}
      />
      <Controller
        control={control}
        name="filters.user"
        render={() => (
          <Autocomplete
            {...register('filters.user')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translation.user}
                id="access-events-user-select-label"
                variant="outlined"
                size="medium"
                placeholder={translation.all_users}
                InputLabelProps={{ shrink: true }}
              />
            )}
            onChange={(_, value) => handleVisitorSelectChange(value as OptionProps<null | string>)}
            value={visitorSelectValue}
            options={visitorSelectOptions}
            id="drawer-input-user"
            isOptionEqualToValue={(option, value) =>
              (option.value === value.value && option.label === value.label) || value === null
            }
            PaperComponent={AutocompletePaper}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            sx={{
              '& .MuiSvgIcon-root': {
                fill: 'white',
                fillOpacity: 0.56
              }
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="filters.locks"
        render={() => (
          <Autocomplete
            {...register('filters.locks')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translation.devices}
                id="access-events-locks-select-label"
                variant="outlined"
                size="medium"
                InputLabelProps={{ shrink: true }}
                placeholder={translation.all_locks}
              />
            )}
            onChange={(_, value) => handleLockSelectChange(value)}
            value={lockSelectValue}
            options={lockSelectOptions}
            id="drawer-input-lock"
            isOptionEqualToValue={(option, value) =>
              (option.value === value.value && option.label === value.label) || value === null
            }
            PaperComponent={AutocompletePaper}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            sx={{
              '& .MuiSvgIcon-root': {
                fill: 'white',
                fillOpacity: 0.56
              }
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="filters.action"
        render={() => (
          <Autocomplete
            {...register('filters.action')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translation.action}
                id="access-events-action-select-label"
                variant="outlined"
                size="medium"
                InputLabelProps={{ shrink: true }}
                placeholder={translation.all_actions}
              />
            )}
            onChange={(_, value) => handleActionSelectChange(value)}
            value={actionSelectValue}
            options={actionSelectOptions}
            id="drawer-input-action"
            isOptionEqualToValue={(option, value) =>
              (option.value === value.value && option.label === value.label) || value === null
            }
            PaperComponent={AutocompletePaper}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            sx={{
              '& .MuiSvgIcon-root': {
                fill: 'white',
                fillOpacity: 0.56
              }
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="filters.reason"
        render={() => (
          <Autocomplete
            {...register('filters.reason')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translation.reason}
                id="access-events-reason-select-label"
                variant="outlined"
                size="medium"
                InputLabelProps={{ shrink: true }}
                placeholder={translation.all_reasons}
              />
            )}
            onChange={(_, value) => handleReasonSelectChange(value)}
            value={reasonSelectValue}
            options={reasonSelectOptions}
            id="drawer-input-reason"
            isOptionEqualToValue={(option, value) =>
              (option.value === value.value && option.label === value.label) || value === null
            }
            PaperComponent={AutocompletePaper}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            sx={{
              '& .MuiSvgIcon-root': {
                fill: 'white',
                fillOpacity: 0.56
              }
            }}
          />
        )}
      />
      <Button
        disabled={!isClearButtonDisabled}
        fullWidth
        variant="contained"
        size="large"
        color="secondary"
        style={isClearButtonDisabled ? { color: theme.palette.text.primary } : { color: theme.palette.text.disabled }}
        onClick={handleClearFiltersButton}
      >
        {translation.reset_filter}
      </Button>
    </ComponentWrapper>
  );
};

export default AccessEventsFilters;
